import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import config from '../../config'
import { dateStringToDisplayDate } from '../../utils/dateUtils'
import { post } from '../../utils/http'
import Filter from '../SessionFilter/SessionFilter'
import SpinnerIcon from '../Icons/SpinnerIcon'
import UserIcon from '../Icons/UserIcon'
import './CustomerList.scss'
import CustomerTags from '../CustomerTags/CustomerTags'
import CountryFlag from '../CountryFlag/CountryFlag'
import { getFilterPayload, useFilterContext } from '../../contexts/filter-context'
import ArrowIconMinimal from '../Icons/ArrowIconMinimal'
import RefreshIcon from '../Icons/RefreshIcon'
import { useDeviceContext } from '../../contexts/device-context'
import { IcaSortingDropDown } from '../IcaElements/IcaSortingDropDown'
import { SortingChangedEvent } from '../Picklist/picklistHelpers'
import { sortByDate } from '../../utils/sortUtils'
import { IcaInputSearch } from '../IcaElements/IcaInputSearch'
import { getCountryCode } from '../../utils/userUtils'
import SearchPlaceholder from '../SearchPlaceholder/SearchPlaceholder'

interface CustomerBrief {
  id: string
  countryCode: string
  acr: string
  latestActivity: string
  banned: boolean
  bannedAt: string
  isFlagged: boolean
}

interface CustomerRequestBody {
  from: string
  to: string
}

export default function Customer() {
  const { deviceState } = useDeviceContext()
  const { filterState } = useFilterContext()
  const [customerList, setCustomerList] = useState<Array<CustomerBrief>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [sortingOptions, setSortingOptions] = useState([
    { id: 'latest-activity-asc', value: 'Senast aktivitet stigande', selected: false },
    { id: 'latest-activity-desc', value: 'Senast aktivitet fallande', selected: true },
  ])
  const [search, setSearch] = useState<string>('')

  const sortedCustomerList = useMemo(() => {
    let result = customerList
     
    if(search) { 
      const query = search.toLowerCase().trim()
      result = result.filter(customer => 
        customer.id.toLowerCase().includes(query)
      )
    }

    const selectedSortingOption = sortingOptions.find(option => option.selected)?.id
    if (selectedSortingOption) {
      switch (selectedSortingOption) {
        case 'latest-activity-asc':
          return sortByDate(result, customer => new Date(customer.latestActivity), 'asc')
        case 'latest-activity-desc':
          return sortByDate(result, customer => new Date(customer.latestActivity), 'desc')
        default:
          return result
      }
    }
    
    return result
  }, [customerList, sortingOptions, search])

  useEffect(() => {
    fetchCustomers()
  }, [filterState.filter])

  const fetchCustomers = async () => {
    if (!filterState.filter?.storeId) {
      setLoading(false)
      return
    }
    else if (filterState.filter.from > filterState.filter.to) {
      setLoading(false)
      return
    }

    setCustomerList([])
    setError(false)
    setLoading(true)
    try {
      const filterPayload: CustomerRequestBody = getFilterPayload(filterState.filter)
      const result = await post<CustomerRequestBody, Array<CustomerBrief>>(`${config.STORE_API_BASE_URL}/v1/monitor/customers/${filterState.filter.storeId}`, filterPayload)
      setCustomerList(result)
    } catch {
      setError(true)
    }
    setLoading(false)
  }

  const handleSortingChange = (e: SortingChangedEvent) => {
    const newOptions = e.detail
    setSortingOptions(newOptions)
  }

  const handleSearchChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.currentTarget.value)
  }

  const handleSearchClear = () => {
    setSearch('')
  }

  return (
    <div className='list-content__container'>
      <div className='list list-content__left customer-info'>
        {!error && <div className={`flex ${deviceState.isMobile ? 'mb-4 items-end' : 'mb-2'}`}>
          <div className={`list-content__count flex ${deviceState.isMobile ? 'flex-col gap-1' : ''}`}>
            <span className='list-content__count-label mr-3'>Visar {sortedCustomerList.length} kunder</span>
            <RefreshIcon size='16px' fontSize='12px' color='var(--color-accent-red)' onClick={fetchCustomers} text='Uppdatera'></RefreshIcon>
          </div>
          <div className={`ml-auto ${deviceState.isMobile ? '' : 'w-[242px]'}`}>
            <IcaSortingDropDown
              options={JSON.stringify(sortingOptions)}
              position="right"
              onSortingchanged={handleSortingChange}
            />
          </div>
        </div>}
        {loading &&
        <div className='grid place-content-center'>
          <SpinnerIcon />
        </div>
        }
        {sortedCustomerList && sortedCustomerList.map((c) =>
          // TODO: Remove acr.
          <div className='list-item customer-info__list-item' key={`${c.id}-${c.acr}-${c.countryCode}`}>
            <Link to={`/customers/${c.id}/${getCountryCode(c.countryCode, c.acr)}${c.acr ? `?acr=${c.acr}` : ''}`} state={{ previousUrl: location.pathname }}>
              <div className='list-item__container'>
                <div className='list-item__left flex items-center'>
                  <div className='shrink-0'><UserIcon /></div>
                  <div className='pl-4'>
                    <div className='customer-info__text-top mb-1 flex flex-wrap break-all items-center'><span className='mr-3'>Kund-id: {c.id}</span>{c.acr && <CountryFlag countryCode={getCountryCode(c.countryCode, c.acr)}/>}</div>
                    <div className='customer-info__text-bottom mt-1'>
                      <span>Senaste aktivitet: </span>
                      <span className='whitespace-nowrap'>{dateStringToDisplayDate(c.latestActivity)}</span>
                    </div>
                  </div>
                </div>
                <div className='list-item__right flex my-auto'>
                  {(c.banned || c.isFlagged) &&
                  <div className='mx-0 md:mx-4 mt-2 md:mt-0'>
                    <CustomerTags isBanned={c.banned} bannedAt={c.bannedAt} isFlagged={c.isFlagged} />
                  </div>}
                </div>
                <div className='list-item__arrow flex items-center'>
                  <ArrowIconMinimal direction='right' size='32px'></ArrowIconMinimal>
                </div>
              </div>
            </Link>
          </div>
        )}
        {!loading && error && <p className='error text-center'>Någonting gick fel. Försök igen genom att ladda om sidan.</p>}
        {!loading && !error && sortedCustomerList.length === 0 && <SearchPlaceholder backgroundColor="var(--color-white)" placeholderText="Inga kunder hittades för angiven butik och tidsintervall." />}
      </div>
      <div className='list-content__right'>
        <div className={`${deviceState.isMobile ? '-mt-3 mb-2' : 'absolute -top-15 w-full'}`}>
          <IcaInputSearch
            label='Sök på kund-id'
            value={search}
            onInput={handleSearchChanged}
            onClear={handleSearchClear}
          />
        </div>
        <Filter />
      </div>
    </div>
  )
}
